import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Label, Modal, Textarea, TextInput } from "flowbite-react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import EventApi from "../../Api/EventApi";
import {
  cancelMeetingRequest,
  confirmMeeting,
  createGuestMeeting,
  createSlot,
  deleteMeeting,
  getMeetingRequests,
  getMeetings,
  getMySchedulePdf,
  patchMeetingDetails,
  requestMeeting,
  rsvpBadgeClass,
  rsvpLabel,
  rsvpMeeting,
  searchInviteCandidates,
  type InviteCandidate,
  type MeetingRequestDetails,
  type ScheduleMeeting,
} from "../../Api/ScheduleApi";
import { Event } from "../../Models/Event";
import { useAppSelector } from "../../Reducers/store";
import Loader from "../Common/Loader";
import {
  formatInstantRange,
  nearestConference,
  wallClockToUtcIso,
  zoneAbbrevAt,
  zoneDisplayName,
} from "../../Utils/timeZone";

type ScheduleTab = "all" | "global" | "my";
type MeetingFilter = "upcoming" | "past" | "cancelled";
type InviteMode = "member" | "guest";

type SelectedInvitee = {
  id: number;
  name: string;
  email: string;
  memberName?: string;
};

function parseUtcInstant(value: string): Date {
  const trimmed = value.trim();
  if (!trimmed) return new Date(NaN);
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return new Date(`${trimmed}T00:00:00Z`);
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed);
  return new Date(hasZone ? trimmed : `${trimmed}Z`);
}

function formatMeetingEt(
  startIso?: string | null,
  endIso?: string | null,
  timeZoneId?: string | null
): string {
  return formatInstantRange(startIso, endIso, timeZoneId);
}

/** Format an API time ("13:15:00" or "13:15") as "1:15 PM". */
function formatWallClock(timeStr: string | null | undefined): string {
  if (!timeStr?.trim()) return "";
  const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return timeStr.trim();
  let hours = Number(match[1]);
  const minutes = match[2];
  if (Number.isNaN(hours) || hours > 23) return timeStr.trim();
  const suffix = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${hours}:${minutes} ${suffix}`;
}

function formatEventDisplay(ev: Event): string {
  if (!ev.eventDate) return "Date TBD";
  try {
    const datePart = ev.eventDate.includes("T") ? ev.eventDate.split("T")[0] : ev.eventDate;
    const [y, mo, d] = datePart.split("-").map(Number);
    const base = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
    const dateLabel = base.toLocaleDateString("en-US", {
      timeZone: "UTC",
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const start = formatWallClock(ev.eventTime);
    const end = formatWallClock(ev.eventEndTime);
    const timePart = start && end ? `${start} – ${end}` : start || end;
    const abbr = ev.eventTime
      ? zoneAbbrevAt(wallClockToUtcIso(datePart, ev.eventTime.slice(0, 5), ev.timeZoneId), ev.timeZoneId)
      : "";
    const clock = timePart ? `${dateLabel}, ${timePart}` : dateLabel;
    return abbr ? `${clock} ${abbr}` : clock;
  } catch {
    return ev.eventDate;
  }
}

function getEventStartMs(ev: Event): number {
  if (!ev.eventDate) return 0;
  try {
    const datePart = ev.eventDate.includes("T") ? ev.eventDate.split("T")[0] : ev.eventDate;
    const [y, mo, d] = datePart.split("-").map(Number);
    let h = 0;
    let m = 0;
    if (ev.eventTime && /^\d{1,2}:\d{2}/.test(ev.eventTime.trim())) {
      const parts = ev.eventTime.trim().split(":").map(Number);
      h = parts[0] ?? 0;
      m = parts[1] ?? 0;
    }
    return Date.parse(wallClockToUtcIso(
      `${y.toString().padStart(4, "0")}-${mo.toString().padStart(2, "0")}-${d.toString().padStart(2, "0")}`,
      `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`,
      ev.timeZoneId
    ));
  } catch {
    return 0;
  }
}

function meetingStatusLabel(status: number) {
  switch (status) {
    case 0:
      return "Scheduled";
    case 1:
      return "Completed";
    case 2:
      return "Cancelled";
    default:
      return `Status ${status}`;
  }
}

function participationDisplay(type: string | null) {
  if (!type) return null;
  if (type === "optional") return "Optional attendance";
  if (type === "required") return "Expected attendance";
  return type;
}

type TimelineItem =
  | { kind: "meeting"; id: number; startMs: number; data: ScheduleMeeting }
  | { kind: "request"; id: number; startMs: number; data: MeetingRequestDetails }
  | { kind: "global"; id: number; startMs: number; data: Event };

export default function SchedulePage() {
  const { userModel } = useAppSelector((s) => s.user);
  const userId = userModel?.id;

  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState<ScheduleMeeting[]>([]);
  const [requests, setRequests] = useState<MeetingRequestDetails[]>([]);
  const [globalEvents, setGlobalEvents] = useState<Event[]>([]);
  const [conferences, setConferences] = useState<Event[]>([]);
  const [scheduleTab, setScheduleTab] = useState<ScheduleTab>("all");
  const [meetingFilter, setMeetingFilter] = useState<MeetingFilter>("upcoming");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [inviteMode, setInviteMode] = useState<InviteMode>("member");
  const [meetingName, setMeetingName] = useState("");
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingTime, setMeetingTime] = useState("");
  const [meetingDuration, setMeetingDuration] = useState("30");
  const [meetingDurationCustom, setMeetingDurationCustom] = useState("");
  const [meetingLocation, setMeetingLocation] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteSearchQuery, setInviteSearchQuery] = useState("");
  const [inviteCandidates, setInviteCandidates] = useState<InviteCandidate[]>([]);
  const [selectedInvitees, setSelectedInvitees] = useState<SelectedInvitee[]>([]);
  const [guestFirstName, setGuestFirstName] = useState("");
  const [guestLastName, setGuestLastName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<ScheduleMeeting | null>(null);
  const [editLocation, setEditLocation] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const reload = useCallback(async () => {
    if (!userId) {
      setMeetings([]);
      setRequests([]);
      setGlobalEvents([]);
      setConferences([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [meetRes, reqRes, eventRes, conferenceRes] = await Promise.all([
        getMeetings().catch(() => [] as ScheduleMeeting[]),
        getMeetingRequests().catch(() => [] as MeetingRequestDetails[]),
        EventApi.getEventList()
          .then((r) => (r.data ?? []).filter((e) => e.isGlobal === true))
          .catch(() => [] as Event[]),
        EventApi.getAllConferences()
          .then((r) => r.data ?? [])
          .catch(() => [] as Event[]),
      ]);
      setMeetings(Array.isArray(meetRes) ? meetRes : []);
      setRequests(Array.isArray(reqRes) ? reqRes : []);
      setGlobalEvents(eventRes);
      setConferences(conferenceRes);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!createOpen || inviteMode !== "member") return;
    const query = inviteSearchQuery.trim();
    if (!query) {
      setInviteCandidates([]);
      return;
    }
    const timer = setTimeout(() => {
      void searchInviteCandidates(query)
        .then((results) => setInviteCandidates(results.filter((u) => u.id !== userId)))
        .catch(() => setInviteCandidates([]));
    }, 350);
    return () => clearTimeout(timer);
  }, [inviteSearchQuery, createOpen, inviteMode, userId]);

  const pendingInvites = useMemo(() => {
    if (!userId) return [];
    return requests.filter((req) => {
      const me = req.participants?.find((p) => p.userId === userId);
      const isInvitee =
        userId === req.recipientUserId || (!!me && me.role !== "organizer");
      if (!isInvitee || userId === req.requesterUserId) return false;
      if (req.status === 0) return !me || me.status === "pending";
      if (req.meetingId && req.myRsvpStatus === "pending") return true;
      return false;
    });
  }, [requests, userId]);

  const filteredMeetings = useMemo(() => {
    const now = Date.now();
    return meetings.filter((meeting) => {
      if (meetingFilter === "cancelled") return meeting.status === 2;
      if (meeting.status === 2) return false;
      const startUtc = meeting.startUtc ?? meeting.createdUtc;
      const startMs = parseUtcInstant(startUtc).getTime();
      const endMs = meeting.endUtc
        ? parseUtcInstant(meeting.endUtc).getTime()
        : startMs + 30 * 60000;
      if (meetingFilter === "past") return endMs < now;
      return endMs >= now;
    });
  }, [meetings, meetingFilter]);

  const filteredRequests = useMemo(() => {
    if (meetingFilter === "cancelled") return [];
    const now = Date.now();
    return requests.filter((req) => {
      if (req.status !== 0) return false;
      if (req.meetingId) return false;
      const endMs = parseUtcInstant(req.endUtc).getTime();
      if (meetingFilter === "past") return endMs < now;
      return endMs >= now;
    });
  }, [requests, meetingFilter]);

  const filteredGlobals = useMemo(() => {
    if (meetingFilter === "cancelled") return [];
    const now = Date.now();
    return globalEvents.filter((ev) => {
      const startMs = getEventStartMs(ev);
      if (!startMs) return meetingFilter === "upcoming";
      if (meetingFilter === "past") return startMs < now;
      return startMs >= now - 2 * 60 * 60 * 1000;
    });
  }, [globalEvents, meetingFilter]);

  const timeline = useMemo(() => {
    const items: TimelineItem[] = [];
    if (scheduleTab === "all" || scheduleTab === "my") {
      for (const m of filteredMeetings) {
        const startMs = parseUtcInstant(m.startUtc ?? m.createdUtc).getTime();
        items.push({ kind: "meeting", id: m.id, startMs, data: m });
      }
      for (const r of filteredRequests) {
        items.push({
          kind: "request",
          id: r.id,
          startMs: parseUtcInstant(r.startUtc).getTime(),
          data: r,
        });
      }
    }
    if (scheduleTab === "all" || scheduleTab === "global") {
      for (const ev of filteredGlobals) {
        items.push({
          kind: "global",
          id: ev.id,
          startMs: getEventStartMs(ev),
          data: ev,
        });
      }
    }
    items.sort((a, b) => a.startMs - b.startMs);
    return items;
  }, [scheduleTab, filteredMeetings, filteredRequests, filteredGlobals]);

  const downloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const response = await getMySchedulePdf();
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(pdfBlob);
      const tempLink = document.createElement("a");
      tempLink.href = url;
      tempLink.setAttribute("download", "EMDA_My_Schedule.pdf");
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Unable to download schedule PDF.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const resetCreateForm = () => {
    setInviteMode("member");
    setMeetingName("");
    setMeetingDate("");
    setMeetingTime("");
    setMeetingDuration("30");
    setMeetingDurationCustom("");
    setMeetingLocation("");
    setInviteMessage("");
    setInviteSearchQuery("");
    setInviteCandidates([]);
    setSelectedInvitees([]);
    setGuestFirstName("");
    setGuestLastName("");
    setGuestPhone("");
    setGuestEmail("");
  };

  const toggleInvitee = (candidate: InviteCandidate) => {
    setSelectedInvitees((prev) => {
      if (prev.some((p) => p.id === candidate.id)) {
        return prev.filter((p) => p.id !== candidate.id);
      }
      return [
        ...prev,
        {
          id: candidate.id,
          name: `${candidate.firstName ?? ""} ${candidate.lastName ?? ""}`.trim(),
          email: candidate.emailAddress ?? "",
          memberName: candidate.memberName ?? undefined,
        },
      ];
    });
  };

  const handleCreate = async () => {
    if (!meetingName.trim()) return toast.error("Please enter a meeting name.");
    if (!meetingDate || !meetingTime) return toast.error("Please select a date and time.");
    if (!meetingLocation.trim()) return toast.error("Please enter a meeting location.");
    if (inviteMode === "member" && selectedInvitees.length === 0) {
      return toast.error("Please select at least one person to invite.");
    }
    if (inviteMode === "guest" && !guestFirstName.trim()) {
      return toast.error("Please enter the guest first name.");
    }

    setSubmitting(true);
    try {
      const durationValue =
        meetingDuration === "other"
          ? Number(meetingDurationCustom || 30)
          : Number(meetingDuration || 30);
      const durationMinutes = Math.max(15, durationValue);
      const booking = nearestConference(conferences, meetingDate);
      const startUtc = wallClockToUtcIso(meetingDate, meetingTime, booking?.timeZoneId);
      const endUtc = new Date(Date.parse(startUtc) + durationMinutes * 60000).toISOString();

      if (inviteMode === "guest") {
        const result = await createGuestMeeting({
          startUtc,
          endUtc,
          meetingName: meetingName.trim(),
          location: meetingLocation.trim(),
          notes: inviteMessage.trim() || null,
          guestFirstName: guestFirstName.trim(),
          guestLastName: guestLastName.trim() || null,
          guestPhone: guestPhone.trim() || null,
          guestEmail: guestEmail.trim() || null,
          conferenceEventId: booking?.id ?? null,
        });
        if (!result.success) {
          toast.error(result.message || "Unable to create guest meeting.");
          return;
        }
        toast.success("Guest meeting created.");
      } else {
        const slotResult = await createSlot({
          startUtc,
          endUtc,
          conferenceEventId: booking?.id ?? null,
        });
        if (!slotResult.success) {
          toast.error(slotResult.message || "Unable to create meeting slot.");
          return;
        }
        const slotId = Number(slotResult.affectedEntityIdentifier);
        const message = [
          `Meeting: ${meetingName}`,
          `Location: ${meetingLocation}`,
          inviteMessage ? `Notes: ${inviteMessage}` : null,
        ]
          .filter(Boolean)
          .join(" | ");
        const requestResult = await requestMeeting({
          slotId,
          recipientUserIds: selectedInvitees.map((i) => i.id),
          message,
        });
        if (!requestResult.success) {
          toast.error(requestResult.message || "Unable to send meeting request.");
          return;
        }
        toast.success("Meeting request sent.");
      }
      setCreateOpen(false);
      resetCreateForm();
      await reload();
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccept = async (req: MeetingRequestDetails) => {
    setBusyId(req.id);
    try {
      if (req.meetingId && req.status !== 0) {
        await rsvpMeeting(req.meetingId, true);
      } else {
        await confirmMeeting(req.id);
      }
      toast.success("Meeting accepted.");
      await reload();
    } catch {
      toast.error("Unable to accept this meeting.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDecline = async (req: MeetingRequestDetails) => {
    if (!window.confirm("Decline this meeting invite?")) return;
    setBusyId(req.id);
    try {
      if (req.meetingId && req.status !== 0) {
        await rsvpMeeting(req.meetingId, false);
      } else {
        await cancelMeetingRequest(req.id);
      }
      toast.success("Meeting declined.");
      await reload();
    } catch {
      toast.error("Unable to decline this meeting.");
    } finally {
      setBusyId(null);
    }
  };

  const handleCancelMeeting = async (meeting: ScheduleMeeting) => {
    if (!window.confirm("Cancel this meeting? Invitees will be notified.")) return;
    setBusyId(meeting.id);
    try {
      const result = await deleteMeeting(meeting.id);
      if (!result.success) {
        toast.error(result.message || "Unable to cancel meeting.");
        return;
      }
      toast.success("Meeting cancelled.");
      await reload();
    } catch {
      toast.error("Unable to cancel meeting.");
    } finally {
      setBusyId(null);
    }
  };

  const openEdit = (m: ScheduleMeeting) => {
    setEditingMeeting(m);
    setEditLocation(m.location ?? "");
    setEditNotes(m.notes ?? "");
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editingMeeting) return;
    setSavingEdit(true);
    try {
      const result = await patchMeetingDetails(editingMeeting.id, {
        location: editLocation.trim() || null,
        notes: editNotes.trim() || null,
      });
      if (!result.success) {
        toast.error(result.message || "Unable to update meeting.");
        return;
      }
      toast.success("Meeting updated.");
      setEditOpen(false);
      setEditingMeeting(null);
      await reload();
    } catch {
      toast.error("Unable to update meeting.");
    } finally {
      setSavingEdit(false);
    }
  };

  const canEditMeeting = (m: ScheduleMeeting) =>
    !!userId && (m.hostUserId === userId || m.guestUserId === userId);

  const tabBtn = (tab: ScheduleTab, label: string) => (
    <button
      type="button"
      key={tab}
      onClick={() => setScheduleTab(tab)}
      className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold ${
        scheduleTab === tab
          ? "border-green-700 bg-green-50 text-green-800"
          : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
      }`}
    >
      {label}
    </button>
  );

  const filterBtn = (filter: MeetingFilter, label: string) => (
    <button
      type="button"
      key={filter}
      onClick={() => setMeetingFilter(filter)}
      className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold ${
        meetingFilter === filter
          ? "border-green-700 bg-green-50 text-green-800"
          : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
      }`}
    >
      {label}
    </button>
  );

  if (!userId) {
    return (
      <div className="p-6">
        <p className="text-gray-600">Please sign in to view your schedule.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-4 md:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Schedule</h1>
          <p className="text-sm text-gray-600">
            Your meetings and EMDA global events ({zoneDisplayName(nearestConference(conferences, meetingDate)?.timeZoneId)} for new bookings)
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button color="light" onClick={downloadPdf} disabled={downloadingPdf}>
            {downloadingPdf ? "Preparing PDF…" : "Print / Email PDF"}
          </Button>
          <Button
            color="success"
            onClick={() => {
              resetCreateForm();
              setCreateOpen(true);
            }}
          >
            Create Meeting
          </Button>
        </div>
      </div>

      {pendingInvites.length > 0 ? (
        <div className="mb-4 rounded-lg border-2 border-amber-400 bg-amber-50 p-4">
          <h2 className="mb-2 text-base font-bold text-amber-950">
            {pendingInvites.length === 1
              ? "1 meeting invite needs your response"
              : `${pendingInvites.length} meeting invites need your response`}
          </h2>
          <ul className="space-y-3">
            {pendingInvites.map((req) => (
              <li
                key={req.id}
                className="rounded-md border border-amber-200 bg-white p-3"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">
                      From {req.requesterName}
                      {req.requesterMemberName ? ` · ${req.requesterMemberName}` : ""}
                    </p>
                    <p className="text-sm text-gray-700">
                      {formatMeetingEt(req.startUtc, req.endUtc, req.timeZoneId)}
                    </p>
                    {req.message ? (
                      <p className="mt-1 line-clamp-2 text-sm text-gray-600">{req.message}</p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      color="success"
                      size="sm"
                      disabled={busyId === req.id}
                      onClick={() => handleAccept(req)}
                    >
                      Accept
                    </Button>
                    <Button
                      color="failure"
                      size="sm"
                      outline
                      disabled={busyId === req.id}
                      onClick={() => handleDecline(req)}
                    >
                      Decline
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mb-3 flex gap-2">{tabBtn("all", "All")}{tabBtn("global", "Global")}{tabBtn("my", "My Meetings")}</div>
      <div className="mb-4 flex gap-2">
        {filterBtn("upcoming", "Upcoming")}
        {filterBtn("past", "Past")}
        {filterBtn("cancelled", "Cancelled")}
      </div>

      {loading ? (
        <Loader />
      ) : timeline.length === 0 ? (
        <p className="text-gray-600">
          {meetingFilter === "cancelled"
            ? "No cancelled meetings."
            : meetingFilter === "past"
              ? "No past meetings."
              : "No upcoming meetings. Create one to get started."}
        </p>
      ) : (
        <ul className="space-y-3">
          {timeline.map((item) => {
            if (item.kind === "global") {
              const ev = item.data;
              return (
                <li
                  key={`g-${ev.id}`}
                  className="rounded-lg border-2 border-blue-400 bg-blue-50 p-4"
                >
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-800">
                    Global Event
                  </div>
                  <h3 className="text-lg font-bold text-blue-900">{ev.name ?? "Global Meeting"}</h3>
                  <p className="text-sm text-blue-900">{formatEventDisplay(ev)}</p>
                  {ev.location ? (
                    <p className="text-sm text-blue-800">Location: {ev.location}</p>
                  ) : null}
                  {ev.description ? (
                    <p className="mt-1 text-sm text-blue-800">{ev.description}</p>
                  ) : null}
                  {participationDisplay(ev.participationType) ? (
                    <p className="mt-1 text-sm font-medium text-blue-900">
                      {participationDisplay(ev.participationType)}
                    </p>
                  ) : null}
                  {ev.parentEventId ? (
                    <Link
                      className="mt-2 inline-block text-sm font-semibold text-blue-700 underline"
                      to={`/events/${ev.parentEventId}/schedule`}
                    >
                      View conference schedule
                    </Link>
                  ) : null}
                </li>
              );
            }

            if (item.kind === "request") {
              const req = item.data;
              const isRequester = userId === req.requesterUserId;
              const inviteeCount =
                req.participants?.filter((p) => p.role !== "organizer").length ?? 1;
              const otherName = isRequester
                ? inviteeCount > 1
                  ? `${inviteeCount} people`
                  : req.recipientName || req.recipientMemberName || "Invitee"
                : req.requesterName || req.requesterMemberName || "Requester";
              return (
                <li
                  key={`r-${req.id}`}
                  className="rounded-lg border border-amber-300 bg-amber-50 p-4"
                >
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-800">
                    Pending Request
                  </div>
                  <h3 className="font-bold text-gray-900">
                    {isRequester ? `With ${otherName}` : `From ${otherName}`}
                  </h3>
                  <p className="text-sm text-gray-700">
                    {formatMeetingEt(req.startUtc, req.endUtc, req.timeZoneId)}
                  </p>
                  {req.message ? (
                    <p className="mt-1 text-sm text-gray-600">{req.message}</p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {!isRequester ? (
                      <>
                        <Button
                          size="sm"
                          color="success"
                          disabled={busyId === req.id}
                          onClick={() => handleAccept(req)}
                        >
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          color="failure"
                          outline
                          disabled={busyId === req.id}
                          onClick={() => handleDecline(req)}
                        >
                          Decline
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        color="failure"
                        outline
                        disabled={busyId === req.id}
                        onClick={() => handleDecline(req)}
                      >
                        Cancel Request
                      </Button>
                    )}
                  </div>
                </li>
              );
            }

            const m = item.data;
            const attendees = m.attendees ?? [];
            return (
              <li key={`m-${m.id}`} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-gray-900">
                      {m.notes?.startsWith("Meeting:")
                        ? m.notes.split("|")[0]?.replace("Meeting:", "").trim() || "Meeting"
                        : "Meeting"}
                    </h3>
                    <p className="text-sm text-gray-700">
                      {formatMeetingEt(m.startUtc, m.endUtc, m.timeZoneId)}
                    </p>
                    <p className="text-sm text-gray-600">
                      Status: {meetingStatusLabel(m.status)}
                      {m.myRsvpStatus ? ` · Your RSVP: ${m.myRsvpStatus}` : ""}
                    </p>
                    {m.location ? (
                      <p className="text-sm text-gray-700">Location: {m.location}</p>
                    ) : null}
                    {m.notes ? (
                      <p className="mt-1 text-sm text-gray-600">{m.notes}</p>
                    ) : null}
                  </div>
                </div>
                {attendees.length > 0 ? (
                  <div className="mt-3">
                    <p className="mb-1 text-xs font-bold uppercase text-gray-500">Participants</p>
                    <ul className="space-y-1">
                      {attendees.map((a) => (
                        <li
                          key={`${m.id}-${a.userId}`}
                          className="flex flex-wrap items-center justify-between gap-2 text-sm"
                        >
                          <span>
                            <span className="font-semibold text-gray-800">{a.name}</span>
                            {a.memberName ? (
                              <span className="text-gray-500"> · {a.memberName}</span>
                            ) : null}
                          </span>
                          <span
                            className={`rounded px-2 py-0.5 text-xs font-semibold ${rsvpBadgeClass(
                              a.status,
                              a.role
                            )}`}
                          >
                            {rsvpLabel(a.status, a.role)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {m.status !== 2 && canEditMeeting(m) ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" color="light" onClick={() => openEdit(m)}>
                      Edit details
                    </Button>
                    <Button
                      size="sm"
                      color="failure"
                      outline
                      disabled={busyId === m.id}
                      onClick={() => handleCancelMeeting(m)}
                    >
                      Cancel meeting
                    </Button>
                  </div>
                ) : null}
                {m.status !== 2 &&
                m.myRsvpStatus === "pending" &&
                m.hostUserId !== userId ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      color="success"
                      disabled={busyId === m.id}
                      onClick={async () => {
                        setBusyId(m.id);
                        try {
                          await rsvpMeeting(m.id, true);
                          toast.success("Accepted.");
                          await reload();
                        } catch {
                          toast.error("Unable to RSVP.");
                        } finally {
                          setBusyId(null);
                        }
                      }}
                    >
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      color="failure"
                      outline
                      disabled={busyId === m.id}
                      onClick={async () => {
                        if (!window.confirm("Decline this meeting?")) return;
                        setBusyId(m.id);
                        try {
                          await rsvpMeeting(m.id, false);
                          toast.success("Declined.");
                          await reload();
                        } catch {
                          toast.error("Unable to RSVP.");
                        } finally {
                          setBusyId(null);
                        }
                      }}
                    >
                      Decline
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <Modal show={createOpen} onClose={() => setCreateOpen(false)} size="lg">
        <Modal.Header>Create Meeting</Modal.Header>
        <Modal.Body>
          <div className="space-y-3">
            <div className="flex gap-2">
              <button
                type="button"
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold ${
                  inviteMode === "member"
                    ? "border-green-700 bg-green-50 text-green-800"
                    : "border-gray-300 bg-white"
                }`}
                onClick={() => setInviteMode("member")}
              >
                Invite member
              </button>
              <button
                type="button"
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold ${
                  inviteMode === "guest"
                    ? "border-green-700 bg-green-50 text-green-800"
                    : "border-gray-300 bg-white"
                }`}
                onClick={() => setInviteMode("guest")}
              >
                Guest meeting
              </button>
            </div>
            <div>
              <Label htmlFor="mtg-name" value="Meeting name" />
              <TextInput
                id="mtg-name"
                value={meetingName}
                onChange={(e) => setMeetingName(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="mtg-date" value={`Date (${zoneDisplayName(nearestConference(conferences, meetingDate)?.timeZoneId)})`} />
                <TextInput
                  id="mtg-date"
                  type="date"
                  value={meetingDate}
                  onChange={(e) => setMeetingDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="mtg-time" value={`Start time (${zoneDisplayName(nearestConference(conferences, meetingDate)?.timeZoneId)})`} />
                <TextInput
                  id="mtg-time"
                  type="time"
                  value={meetingTime}
                  onChange={(e) => setMeetingTime(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="mtg-duration" value="Duration (minutes)" />
                <select
                  id="mtg-duration"
                  className="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm"
                  value={meetingDuration}
                  onChange={(e) => setMeetingDuration(e.target.value)}
                >
                  <option value="15">15</option>
                  <option value="30">30</option>
                  <option value="45">45</option>
                  <option value="60">60</option>
                  <option value="other">Other</option>
                </select>
              </div>
              {meetingDuration === "other" ? (
                <div>
                  <Label htmlFor="mtg-duration-custom" value="Custom minutes" />
                  <TextInput
                    id="mtg-duration-custom"
                    type="number"
                    min={15}
                    value={meetingDurationCustom}
                    onChange={(e) => setMeetingDurationCustom(e.target.value)}
                  />
                </div>
              ) : null}
            </div>
            <div>
              <Label htmlFor="mtg-location" value="Location" />
              <TextInput
                id="mtg-location"
                value={meetingLocation}
                onChange={(e) => setMeetingLocation(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="mtg-notes" value="Notes / message" />
              <Textarea
                id="mtg-notes"
                rows={3}
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
              />
            </div>

            {inviteMode === "member" ? (
              <div>
                <Label htmlFor="mtg-search" value="Search people to invite" />
                <TextInput
                  id="mtg-search"
                  placeholder="Name or email"
                  value={inviteSearchQuery}
                  onChange={(e) => setInviteSearchQuery(e.target.value)}
                />
                {selectedInvitees.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedInvitees.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-900"
                        onClick={() =>
                          setSelectedInvitees((prev) => prev.filter((p) => p.id !== s.id))
                        }
                      >
                        {s.name || s.email} ×
                      </button>
                    ))}
                  </div>
                ) : null}
                {inviteCandidates.length > 0 ? (
                  <ul className="mt-2 max-h-40 overflow-y-auto rounded border border-gray-200">
                    {inviteCandidates.map((c) => {
                      const selected = selectedInvitees.some((s) => s.id === c.id);
                      const name = `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim();
                      return (
                        <li key={c.id}>
                          <button
                            type="button"
                            className={`flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                              selected ? "bg-green-50" : ""
                            }`}
                            onClick={() => toggleInvitee(c)}
                          >
                            <span className="font-semibold">{name || c.emailAddress}</span>
                            <span className="text-xs text-gray-500">
                              {[c.emailAddress, c.memberName].filter(Boolean).join(" · ")}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="guest-first" value="Guest first name" />
                  <TextInput
                    id="guest-first"
                    value={guestFirstName}
                    onChange={(e) => setGuestFirstName(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="guest-last" value="Guest last name" />
                  <TextInput
                    id="guest-last"
                    value={guestLastName}
                    onChange={(e) => setGuestLastName(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="guest-phone" value="Guest phone" />
                  <TextInput
                    id="guest-phone"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="guest-email" value="Guest email" />
                  <TextInput
                    id="guest-email"
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="success" onClick={handleCreate} disabled={submitting}>
            {submitting ? "Saving…" : inviteMode === "guest" ? "Create guest meeting" : "Send invite"}
          </Button>
          <Button color="gray" onClick={() => setCreateOpen(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={editOpen} onClose={() => setEditOpen(false)}>
        <Modal.Header>Edit meeting details</Modal.Header>
        <Modal.Body>
          <div className="space-y-3">
            <div>
              <Label htmlFor="edit-location" value="Location" />
              <TextInput
                id="edit-location"
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="edit-notes" value="Notes" />
              <Textarea
                id="edit-notes"
                rows={4}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="success" onClick={saveEdit} disabled={savingEdit}>
            {savingEdit ? "Saving…" : "Save"}
          </Button>
          <Button color="gray" onClick={() => setEditOpen(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
